import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Split, Plus, Loader2, Trash2, ArrowRight } from "lucide-react";

const SOURCES = ["", "Meta Lead Ads", "WhatsApp", "Website Form", "Referral", "Manual"];
const ASSIGNMENTS = ["round_robin", "load_balanced", "skill_based", "availability_based", "geography_based", "product_based", "language_based", "shift_based"];

export default function Routing() {
  const qc = useQueryClient();
  const { tenant } = useAuth();
  const bus = tenant?.settings?.business_units || [];
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    name: "", priority: 1, active: true,
    match: { source: "", meta_page: "", campaign: "" },
    then: { business_unit: "", team: "", assignment: "round_robin", sla_minutes: 15 },
  });
  const setMatch = (k, v) => setF((p) => ({ ...p, match: { ...p.match, [k]: v } }));
  const setThen = (k, v) => setF((p) => ({ ...p, then: { ...p.then, [k]: v } }));

  const { data: rules = [], isLoading } = useQuery({ queryKey: ["routing-rules"], queryFn: async () => (await api.get("/routing-rules")).data });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/routing-rules", { ...body, status: "active" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["routing-rules"] }); setOpen(false); toast.success("Routing rule created"); },
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, active }) => api.patch(`/routing-rules/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["routing-rules"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/routing-rules/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["routing-rules"] }); toast.success("Rule deleted"); },
  });

  const chip = (label, val) => val ? <span key={label} className="rounded bg-muted px-1.5 py-0.5 text-xs">{label}: {val}</span> : null;

  return (
    <div>
      <PageHeader title="Lead Routing Engine" subtitle="Auto-route incoming leads by page, source & campaign" testid="routing-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-routing-btn"><Plus className="mr-1.5 h-4 w-4" /> New Rule</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Create routing rule</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Rule name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Logistic leads → Logistic Sales" data-testid="routing-name-input" /></div>
                <div className="space-y-1.5"><Label>Priority</Label><Input type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: Number(e.target.value) })} data-testid="routing-priority-input" /></div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-info">IF (match)</p>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1.5"><Label>Source</Label>
                    <Select value={f.match.source || "any"} onValueChange={(v) => setMatch("source", v === "any" ? "" : v)}>
                      <SelectTrigger data-testid="routing-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="any">Any source</SelectItem>{SOURCES.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Meta Page</Label><Input value={f.match.meta_page} onChange={(e) => setMatch("meta_page", e.target.value)} placeholder="Certon Logistic (blank = any)" data-testid="routing-page-input" /></div>
                  <div className="space-y-1.5"><Label>Campaign</Label><Input value={f.match.campaign} onChange={(e) => setMatch("campaign", e.target.value)} placeholder="Optional" /></div>
                </div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-automation">THEN (route)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Business Unit</Label>
                    {bus.length ? (
                      <Select value={f.then.business_unit || "none"} onValueChange={(v) => setThen("business_unit", v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="routing-bu-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">—</SelectItem>{bus.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <Input value={f.then.business_unit} onChange={(e) => setThen("business_unit", e.target.value)} />}
                  </div>
                  <div className="space-y-1.5"><Label>Team</Label><Input value={f.then.team} onChange={(e) => setThen("team", e.target.value)} placeholder="Logistic Sales" data-testid="routing-team-input" /></div>
                  <div className="space-y-1.5"><Label>Assignment</Label>
                    <Select value={f.then.assignment} onValueChange={(v) => setThen("assignment", v)}>
                      <SelectTrigger data-testid="routing-assignment-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ASSIGNMENTS.map((a) => <SelectItem key={a} value={a} className="capitalize">{a.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>SLA (minutes)</Label><Input type="number" value={f.then.sla_minutes} onChange={(e) => setThen("sla_minutes", Number(e.target.value))} data-testid="routing-sla-input" /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={() => createMut.mutate(f)} disabled={createMut.isPending || !f.name} data-testid="routing-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create rule"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isLoading ? <Loading /> : rules.length === 0 ? (
        <EmptyState icon={Split} title="No routing rules yet" description="Create a rule to automatically assign incoming leads to the right business unit, team and owner with an SLA."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> New Rule</Button>} testid="routing-empty" />
      ) : (
        <div className="space-y-3">
          {[...rules].sort((a, b) => (a.priority || 100) - (b.priority || 100)).map((r) => (
            <Card key={r.id} className="p-4" data-testid={`routing-rule-${r.id}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Split className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{r.name}</p>
                    <Badge variant="secondary">P{r.priority}</Badge>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase text-info">IF</span>
                    {chip("source", r.match?.source)}{chip("page", r.match?.meta_page)}{chip("campaign", r.match?.campaign)}
                    {!r.match?.source && !r.match?.meta_page && !r.match?.campaign && <span className="text-xs text-muted-foreground">any lead</span>}
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[11px] font-semibold uppercase text-automation">THEN</span>
                    {chip("BU", r.then?.business_unit)}{chip("team", r.then?.team)}
                    <span className="rounded bg-automation/10 px-1.5 py-0.5 text-xs capitalize text-automation">{(r.then?.assignment || "round robin").replace(/_/g, " ")}</span>
                    <span className="rounded bg-warning/10 px-1.5 py-0.5 text-xs text-warning">SLA {r.then?.sla_minutes || 15}m</span>
                  </div>
                </div>
                <Switch checked={r.active} onCheckedChange={(v) => toggleMut.mutate({ id: r.id, active: v })} data-testid={`routing-toggle-${r.id}`} />
                <Button variant="ghost" size="icon" onClick={() => deleteMut.mutate(r.id)} data-testid={`routing-delete-${r.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
